import { useState } from 'react';
import { Icon } from '../../../layout/Icon.jsx';
import { toast } from '../../../shared/useToast.js';

/**
 * طباعة وتصدير ديون العملاء — بنفس نهج `reports/components/ExportButton.jsx`
 * (استيراد كسول لمكتبة xlsx الموجودة أصلاً في المشروع) وبنفس نهج
 * `print-root` + `print.css` للطباعة. لا مكتبة جديدة.
 *
 * التصدير والطباعة يأخذان الصفوف المعروضة فعلاً تحت الفلاتر النشطة — نفس
 * المصفوفة التي يرسمها الجدول — فلا يمكن أن يختلف الملف عمّا يراه المستخدم.
 *
 * ملاحظة الصلاحية: لا نستعمل `ExportButton` نفسه لأنه محجوب بـ
 * `reports.export`، وموظف تحصيل الديون لا يملكها عادةً فكان الزر يختفي عنه.
 */

/** الأرقام تُكتب أرقاماً في الملف لا نصاً، مع تنسيق عملة — فتُجمع في Excel. */
const IQD_FORMAT = '#,##0" د.ع"';

function cellValue(row, col) {
  return typeof col.value === 'function' ? col.value(row) : row[col.key];
}

async function exportExcel({ rows, columns, filename, title, meta }) {
  const XLSX = await import('xlsx');

  const header = columns.map((c) => c.label);
  const body = rows.map((r) => columns.map((c) => cellValue(r, c)));

  // سطور الترويسة: العنوان والفلاتر المطبَّقة — حتى يُعرف الملف بعد حفظه.
  const head = [[title]];
  (meta || []).forEach((line) => head.push([line]));
  head.push([]);

  const ws = XLSX.utils.aoa_to_sheet([...head, header, ...body]);
  // ملاحظة: نسخة xlsx المجتمعية (0.18.5) لا تكتب اتجاه الورقة فعلياً، لكن
  // المحتوى عربي والأعمدة معنونة فيُقرأ سليماً. نتركها لتعمل عند الترقية.
  ws['!rtl'] = true;
  ws['!cols'] = columns.map((c) => ({ wch: c.width || 16 }));

  const headerRows = head.length;
  // تنسيق أعمدة المبالغ: الخلية رقم + تنسيق عملة (لا نص «12,000 د.ع»).
  columns.forEach((col, ci) => {
    if (!col.money) return;
    for (let ri = 0; ri < body.length; ri += 1) {
      const ref = XLSX.utils.encode_cell({ r: headerRows + 1 + ri, c: ci });
      const cell = ws[ref];
      if (cell && typeof cell.v === 'number') {
        cell.t = 'n';
        cell.z = IQD_FORMAT;
      }
    }
  });

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'ديون العملاء');
  XLSX.writeFile(wb, filename.endsWith('.xlsx') ? filename : `${filename}.xlsx`);
}

export function DebtsExportButtons({ rows, columns, filename, title, meta, disabled }) {
  const [busy, setBusy] = useState(false);
  const hasData = Array.isArray(rows) && rows.length > 0;

  async function onExcel() {
    if (!hasData) {
      toast.error('لا توجد بيانات للتصدير');
      return;
    }
    setBusy(true);
    try {
      await exportExcel({ rows, columns, filename, title, meta });
      toast.success(`تم تصدير ${rows.length} سجلاً إلى Excel`);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('debts export error:', err);
      toast.error('تعذّر التصدير');
    } finally {
      setBusy(false);
    }
  }

  function onPrint() {
    if (!hasData) {
      toast.error('لا توجد بيانات للطباعة');
      return;
    }
    window.print();
  }

  return (
    <>
      <button type="button" className="btn" onClick={onPrint} disabled={disabled || busy}>
        <Icon name="print" size={13} /> طباعة
      </button>
      <button type="button" className="btn" onClick={onExcel} disabled={disabled || busy}>
        <Icon name="reports" size={13} /> {busy ? 'جاري التصدير...' : 'تصدير Excel'}
      </button>
    </>
  );
}
